import React from 'react';

const Terms: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-8">Terms of Service</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-8">Last updated: December 2024</p>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              By accessing and using Lunexa's services, website, or any related platforms, you accept and agree to be bound by these 
              Terms of Service. If you do not agree to these terms, please do not use our services. These terms apply to all 
              visitors, users, and others who access or use our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">2. Description of Services</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Lunexa is a technology company that provides comprehensive IT solutions including:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>IT automation and process optimization</li>
              <li>Web and mobile application development</li>
              <li>Artificial intelligence and machine learning solutions</li>
              <li>E-commerce and marketplace management</li>
              <li>Digital transformation consulting</li>
              <li>Cloud solutions and system integration</li>
            </ul>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We reserve the right to modify, suspend, or discontinue any aspect of our services at any time with reasonable notice.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">3. User Responsibilities</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">By using our services, you agree to:</p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Provide accurate, current, and complete information during registration and use</li>
              <li>Maintain the security and confidentiality of your account credentials</li>
              <li>Use our services in compliance with all applicable laws and regulations</li>
              <li>Not interfere with, disrupt, or attempt to gain unauthorized access to our services</li>
              <li>Not use our services for any unlawful, harmful, or fraudulent purposes</li>
              <li>Respect the intellectual property rights of Lunexa and third parties</li>
              <li>Not transmit any malicious code, viruses, or harmful content</li>
              <li>Comply with any additional terms specific to particular services</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">4. Intellectual Property Rights</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              All content, features, functionality, designs, logos, trademarks, and other materials provided through our services 
              are owned by Lunexa or our licensors and are protected by copyright, trademark, patent, and other intellectual 
              property laws.
            </p>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              You may not reproduce, distribute, modify, create derivative works, publicly display, publicly perform, republish, 
              download, store, or transmit any of our content without our express written permission, except as permitted by 
              applicable law or these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">5. Payment Terms and Billing</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Payment terms, pricing, and billing procedures will be specified in individual service agreements or project contracts. 
              Key payment terms include:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>All fees are due as specified in your service agreement</li>
              <li>Payments are typically required before project commencement or as per milestone schedule</li>
              <li>Late payments may incur additional charges as specified in your agreement</li>
              <li>We reserve the right to suspend services for non-payment</li>
              <li>Pricing changes will be communicated with reasonable advance notice</li>
              <li>Refunds are subject to our Refund Policy</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">6. Service Availability and Warranties</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              While we strive to provide reliable and high-quality services, we cannot guarantee uninterrupted or error-free operation. 
              Our services are provided "as is" and "as available" without warranties of any kind, either express or implied.
            </p>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We disclaim all warranties, including but not limited to implied warranties of merchantability, fitness for a particular 
              purpose, and non-infringement. We do not warrant that our services will meet your requirements or be available on an 
              uninterrupted, secure, or error-free basis.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">7. Limitation of Liability</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              To the maximum extent permitted by applicable law, Lunexa shall not be liable for any indirect, incidental, special, 
              consequential, or punitive damages, including but not limited to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Loss of profits, revenue, or business opportunities</li>
              <li>Loss of data or information</li>
              <li>Business interruption or downtime</li>
              <li>Loss of goodwill or reputation</li>
              <li>Any other intangible losses</li>
            </ul>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Our total liability for any claims arising from or related to these Terms or our services shall not exceed the amount 
              paid by you to Lunexa in the twelve (12) months preceding the claim.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">8. Indemnification</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              You agree to defend, indemnify, and hold harmless Lunexa, its officers, directors, employees, agents, and affiliates 
              from and against any claims, damages, obligations, losses, liabilities, costs, or debt, and expenses (including 
              reasonable attorney's fees) arising from:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Your use or misuse of our services</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Any content you submit or transmit through our services</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">9. Termination</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Either party may terminate these Terms at any time. We may terminate or suspend your access to our services 
              immediately, with or without notice, for any reason, including but not limited to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Breach of these Terms</li>
              <li>Non-payment of fees</li>
              <li>Fraudulent or illegal activity</li>
              <li>Extended periods of inactivity</li>
            </ul>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Upon termination, your right to use our services will cease immediately, and we may delete your account and data.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">10. Privacy and Data Protection</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Your privacy is important to us. Our collection, use, and protection of your personal information is governed by our 
              Privacy Policy, which is incorporated into these Terms by reference. By using our services, you consent to the 
              collection and use of your information as described in our Privacy Policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">11. Governing Law and Dispute Resolution</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              These Terms shall be interpreted and governed by applicable laws, without regard to conflict of law provisions. 
              Any disputes arising from these Terms or our services shall be resolved through good faith negotiation first.
            </p>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              If a dispute cannot be resolved through negotiation, it may be subject to binding arbitration or resolved in 
              competent courts, as determined by the nature and jurisdiction of the dispute.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">12. Changes to Terms</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We reserve the right to modify these Terms at any time. When we make material changes, we will:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Update the "Last updated" date at the top of these Terms</li>
              <li>Notify you via email or through our services</li>
              <li>Provide reasonable notice before changes take effect</li>
            </ul>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Your continued use of our services after changes become effective constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">13. Miscellaneous</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              These Terms constitute the entire agreement between you and Lunexa regarding our services. If any provision is found 
              to be unenforceable, the remaining provisions will remain in full force and effect. Our failure to enforce any right 
              or provision will not constitute a waiver of such right or provision.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">14. Contact Information</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="text-gray-600 dark:text-gray-400">
                <strong>Email:</strong> support@lunexa.dpdns.org<br />
                <strong>Website:</strong> www.lunexa.com<br />
                <strong>Business Hours:</strong> Monday - Friday, 9:00 AM - 6:00 PM
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Terms;